import { useLoadoutStore } from '../store/loadoutSlice';
import { SKETCH_REGISTRY } from '../data/sketches.config';
import type { Element } from '../types/enums';
import type {
  SketchDefinition,
  SketchModelId,
  SketchSlotName,
} from '../types/sketch.types';
import { SKETCH_SLOT_NAMES } from '../types/sketch.types';
import { liveLoadoutSource, type LoadoutSource } from './LoadoutSourceService';

/**
 * Smart layer around `useLoadoutStore` for sketches. Encapsulates the
 * per-element anti-duplicate rule on the equipped loadout.
 */

/** Resolves the reinforced element of an equipped sketch. */
function getEquippedSketchElement(slot: SketchSlotName): Element | null {
  const store = useLoadoutStore.getState();
  const modelId = store.equipped.sketches[slot];
  if (!modelId || !store.hasSketch(modelId)) {
    return null;
  }
  return SKETCH_REGISTRY[modelId]?.emotion ?? null;
}

/**
 * True if the target slot can hold an instance of this element without
 * creating a duplicate on the equipped loadout. The other slot is checked.
 */
export function canEquipSketchElement(
  targetSlot: SketchSlotName,
  element: Element,
): boolean {
  for (const slot of SKETCH_SLOT_NAMES) {
    if (slot === targetSlot) {
      continue;
    }
    if (getEquippedSketchElement(slot) === element) {
      return false;
    }
  }
  return true;
}

/**
 * Equips a sketch in a given slot. Checks the element anti-duplicate
 * rule against the other slot; returns `false` on conflict (caller must
 * unequip the other slot first, or pick another sketch). If the sketch
 * is already equipped in the other slot, it is removed there first — a
 * move from one slot to the other is always allowed even when the
 * element is the same (it moves, it does not duplicate).
 */
export function equipSketch(
  slot: SketchSlotName,
  modelId: SketchModelId,
): boolean {
  const store = useLoadoutStore.getState();
  if (!store.hasSketch(modelId)) {
    return false;
  }
  const def = SKETCH_REGISTRY[modelId];
  if (!def) {
    return false;
  }

  // Same-sketch move first: free the other slot before the anti-duplicate
  // check, otherwise a legitimate move would be rejected.
  for (const other of SKETCH_SLOT_NAMES) {
    if (other !== slot && store.equipped.sketches[other] === modelId) {
      store.equipSketch(other, null);
    }
  }

  if (!canEquipSketchElement(slot, def.emotion)) {
    return false;
  }

  store.equipSketch(slot, modelId);
  return true;
}

/** Unequips the given sketch slot. No-op if already empty. */
export function unequipSketch(slot: SketchSlotName): void {
  useLoadoutStore.getState().equipSketch(slot, null);
}

export function iterateEquippedSketches(
  source: LoadoutSource = liveLoadoutSource(),
): { def: SketchDefinition }[] {
  const out: { def: SketchDefinition }[] = [];
  for (const modelId of source.sketches) {
    const def = SKETCH_REGISTRY[modelId];
    if (def) {
      out.push({ def });
    }
  }
  return out;
}

export function getEquippedEmotionTypes(
  source: LoadoutSource = liveLoadoutSource(),
): Element[] {
  const out: Element[] = [];
  for (const { def } of iterateEquippedSketches(source)) {
    if (!out.includes(def.emotion)) {
      out.push(def.emotion);
    }
  }
  return out.slice(0, 2);
}
